import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { supabase, Project } from '../../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';

export default function ProjectsManagePage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    category: 'Web',
    tech_stack: '',
    live_demo_url: '',
    github_url: '',
    featured: true,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Gagal memuat projek');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const techStackArray = formData.tech_stack.split(',').map((t) => t.trim()).filter(Boolean);

    try {
      if (editingProject) {
        // Update
        const { error } = await supabase
          .from('projects')
          .update({
            title: formData.title,
            slug: formData.slug,
            description: formData.description,
            category: formData.category,
            tech_stack: techStackArray,
            live_demo_url: formData.live_demo_url,
            github_url: formData.github_url,
            featured: formData.featured,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingProject.id);

        if (error) throw error;
        toast.success('Projek berhasil diupdate!');
      } else {
        // Create
        const { error } = await supabase.from('projects').insert([
          {
            title: formData.title,
            slug: formData.slug,
            description: formData.description,
            category: formData.category,
            tech_stack: techStackArray,
            live_demo_url: formData.live_demo_url,
            github_url: formData.github_url,
            featured: formData.featured,
            order_index: projects.length,
          },
        ]);

        if (error) throw error;
        toast.success('Projek berhasil ditambahkan!');
      }

      resetForm();
      fetchProjects();
    } catch (error: any) {
      toast.error('Gagal menyimpan projek: ' + error.message);
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      slug: project.slug,
      description: project.description,
      category: project.category,
      tech_stack: project.tech_stack.join(', '),
      live_demo_url: project.live_demo_url || '',
      github_url: project.github_url || '',
      featured: project.featured,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Yakin ingin menghapus projek ini?')) return;

    try {
      const { error } = await supabase.from('projects').delete().eq('id', id);

      if (error) throw error;
      toast.success('Projek berhasil dihapus!');
      fetchProjects();
    } catch (error) {
      toast.error('Gagal menghapus projek');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      description: '',
      category: 'Web',
      tech_stack: '',
      live_demo_url: '',
      github_url: '',
      featured: true,
    });
    setEditingProject(null);
    setShowForm(false);
  };

  return (
    <div>
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Kelola Projek</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-neon flex items-center gap-2"
        >
          <Plus size={20} />
          Tambah Projek
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 mb-6"
        >
          <h2 className="text-xl font-bold text-white mb-4">
            {editingProject ? 'Edit Projek' : 'Tambah Projek Baru'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Judul Projek"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              />
              <input
                type="text"
                placeholder="Slug (URL-friendly)"
                required
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              />
            </div>

            <textarea
              placeholder="Deskripsi Projek"
              required
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue resize-none"
            />

            <div className="grid md:grid-cols-2 gap-4">
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              >
                <option value="Web">Web</option>
                <option value="No-Code">No-Code</option>
                <option value="AI">AI</option>
                <option value="Law-Tech">Law-Tech</option>
                <option value="Web3">Web3</option>
              </select>

              <input
                type="text"
                placeholder="Tech Stack (pisahkan dengan koma)"
                required
                value={formData.tech_stack}
                onChange={(e) => setFormData({ ...formData, tech_stack: e.target.value })}
                className="px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="url"
                placeholder="Live Demo URL"
                value={formData.live_demo_url}
                onChange={(e) => setFormData({ ...formData, live_demo_url: e.target.value })}
                className="px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              />

              <input
                type="url"
                placeholder="GitHub URL"
                value={formData.github_url}
                onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                className="px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue"
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                className="w-5 h-5"
              />
              <span className="text-white">Tampilkan sebagai projek unggulan</span>
            </label>

            <div className="flex gap-3">
              <button type="submit" className="btn-neon-green">
                {editingProject ? 'Update' : 'Simpan'}
              </button>
              <button type="button" onClick={resetForm} className="btn-neon">
                Batal
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Projects List */}
      {loading ? (
        <div className="text-center text-gray-400 py-12">Memuat projek...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ y: -5 }}
              className="glass-card p-6"
            >
              <div className="flex items-start justify-between mb-3">
                <span className="px-3 py-1 bg-neon-blue/20 text-neon-blue text-xs font-bold rounded-full">
                  {project.category}
                </span>
                {project.featured && (
                  <span className="px-2 py-1 bg-neon-green/20 text-neon-green text-xs rounded">
                    Featured
                  </span>
                )}
              </div>

              <h3 className="text-lg font-bold text-white mb-2">{project.title}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">{project.description}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                {project.tech_stack.slice(0, 3).map((tech) => (
                  <span
                    key={tech}
                    className="px-2 py-1 bg-dark-lighter text-neon-green text-xs rounded"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(project)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-neon-blue/10 text-neon-blue rounded-lg hover:bg-neon-blue/20 transition"
                >
                  <Edit size={16} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(project.id)}
                  className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition"
                >
                  <Trash2 size={16} />
                </button>
                {project.live_demo_url && (
                  <a
                    href={project.live_demo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-neon-green/10 text-neon-green rounded-lg hover:bg-neon-green/20 transition"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
